import { sleep } from 'utils/async';
import { isElemScrolledToBottom, stopAllScrollObservers } from './utils';
import {
  getLastMessageFromAuthor,
  getMessageHtmlElem,
  scrollToMessagesListBottom,
} from 'utils/message';
import {
  observerOptions,
  PX_TOLERANCE_IS_BOTTOM,
  RATIO_SHOW_CTA_LATEST_MSG,
  TIME_BUFFER_ASYNC_SCROLL_UX,
} from './config';
import { ACTIONS } from 'constants/hook';
import { CHATUI_WINDOW_PATH } from 'constants/global';
import { PARTICIPANT_PREFIX } from 'constants/twilio';
import { IObserverAutoScrollProps } from './types';

/**
 * Create a dynamic Intersection Observer instance for when user is already at the bottom of conversation
 * message thread. If participant message is too long, view will scroll near top for best viewing.
 *
 * It will auto regenerate then self-destruct whenever new message is received.
 */
export const observerAutoScroll = async ({
  dispatchViewAction,
  isAdvocateInConvo,
  isAdvocateInConvoAndLastMsgByUser,
  mainContentRef,
  msgCount,
  msgLastObsAutoScrollRef,
}: IObserverAutoScrollProps) => {
  /** START scoped callback function **/
  const obsAutoScrollCallback = async (obs: Array<IntersectionObserverEntry>) => {
    const lastMsgObs = obs[0];

    if (isAdvocateInConvo) {
      // Message finished parsing. If message is short, autoscroll to bottom. Otherwise, show CTA arrow to symbolize overflow.
      if (lastMsgObs.intersectionRatio > RATIO_SHOW_CTA_LATEST_MSG) {
        // Autoscroll 2 of 2.
        scrollToMessagesListBottom();
      }
    }
    stopAllScrollObservers(msgLastObsAutoScrollRef, msgCount); // need second invocation to ensure truly gone. Because of async timing of message parsing.
    setTimeout(() => {
      // intersectionRatio will always start at 1, as precursor logic in this function will focus on last message.
      // After timeout, message wil lbe finished rendering and reflect the true value.
      // console.log('after dust settled in autoscroll. IntersectionRatio:', lastMsgObs.intersectionRatio);
      if (lastMsgObs.intersectionRatio < 1) {
        const latestViewState = window[CHATUI_WINDOW_PATH].getViewState();
        const nextViewState = { ...latestViewState };
        const isShortContent = lastMsgObs.intersectionRatio >= RATIO_SHOW_CTA_LATEST_MSG;

        if (!isShortContent) {
          // console.log('ChatUI auto scroll scenario 0 - show CTA');
          const isScrolledToBottom = isElemScrolledToBottom(
            mainContentRef.current,
            PX_TOLERANCE_IS_BOTTOM,
          );
          nextViewState.scrollToBottom.isCTAVisible = isScrolledToBottom ? false : !isShortContent;
          nextViewState.scrollToBottom.hasSeenCTAThisMsg = !isShortContent;
          dispatchViewAction({ type: ACTIONS.CHANGE, payload: nextViewState });
        }
      }
    }, TIME_BUFFER_ASYNC_SCROLL_UX);
  };
  /** END scoped callback function **/

  // Don't execute any scrolling manipulation logic while user is adding more messages.
  if (isAdvocateInConvoAndLastMsgByUser) {
    return;
  }

  // Handle new advocate message. Show typing animation. Autoscroll 1 of 2.
  if (isAdvocateInConvo) {
    scrollToMessagesListBottom();
  }

  await sleep(TIME_BUFFER_ASYNC_SCROLL_UX); // magic number. Be careful about changing.

  const msgLastByUser = await getLastMessageFromAuthor(PARTICIPANT_PREFIX.CUSTOMER);
  if (msgLastByUser) {
    const elemMsgLastByUser = getMessageHtmlElem.byIndex(msgLastByUser.index);
    const isScrollToMsg = isAdvocateInConvo ? msgLastByUser.index === msgCount - 2 : true;
    isScrollToMsg &&
      elemMsgLastByUser &&
      elemMsgLastByUser.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  stopAllScrollObservers(msgLastObsAutoScrollRef, msgCount);
  msgLastObsAutoScrollRef.current = new IntersectionObserver(
    obsAutoScrollCallback,
    observerOptions, // see if threshold can be reduced
  );

  const elemMsgLastInConvo = getMessageHtmlElem.byIndex(msgCount - 1);
  elemMsgLastInConvo && msgLastObsAutoScrollRef.current?.observe(elemMsgLastInConvo);
};
